import Box from '@mui/material/Box';
import {
  DataGrid,
  GridColDef,
  GridFilterModel,
  GridRenderCellParams,
} from '@mui/x-data-grid';
import { useMemo } from 'react';
import {
  DEFAULT_CONVERSATIONS_SORT,
  DEFAULT_PAGE_SIZE,
  SEARCH_MAX_LENGTH,
} from '../../../constants/dashboardConstants';
import { languageLabel } from '../../../helpers/chatDashboardHelper';
import {
  formatUnixDate,
  isDashboardSortable,
} from '../../../helpers/dashboardHelper';
import { ServerPaging } from '../../../interfaces/DashboardPaging';
import { useTranslation } from '../../../hooks/useTranslation';
import { AgentType } from '../../../interfaces/Agent';
import { Conversation } from '../../../interfaces/Analytics';
import ConversationDetailsModal from '../Modal/ConversationDetailsModal';
import ConversationsTableToolbar from './ConversationsTableToolbar';

const CALL_VISIBLE_FIELDS = [
  'start_ts',
  'agent_name',
  'caller_phone_number',
  'conversation_duration',
  'status',
  'data_collected',
];

const CHAT_VISIBLE_FIELDS = [
  'start_ts',
  'agent_name',
  'messages_count',
  'conversation_duration',
  'conversation_language',
  'data_collected',
];

// Every change is a backend call, so wait longer than the grid's 150ms
const SERVER_SEARCH_DEBOUNCE_MS = 500;

interface ConversationsTableProps {
  conversations: Conversation[];
  agentType?: AgentType;
  serverPaging?: ServerPaging;
}

const ConversationsTable: React.FC<ConversationsTableProps> = ({
  conversations,
  agentType = AgentType.CALL,
  serverPaging,
}) => {
  const { t } = useTranslation();

  const visibleFields =
    agentType === AgentType.CALL ? CALL_VISIBLE_FIELDS : CHAT_VISIBLE_FIELDS;

  const columns = [
    {
      field: 'start_ts',
      headerName: t('Date & Time'),
      width: 200,
    },
    {
      field: 'agent_name',
      headerName: t('Agent'),
      width: 150,
    },
    ...(agentType === AgentType.CALL
      ? [
          {
            field: 'caller_phone_number',
            headerName: t('Contact'),
            width: 150,
            groupable: false,
          },
        ]
      : [
          {
            field: 'messages_count',
            headerName: t('Messages'),
            width: 120,
            groupable: false,
          },
        ]),
    {
      field: 'conversation_duration',
      headerName: t('Duration'),
      width: 100,
    },
    ...(agentType === AgentType.CALL
      ? [
          {
            field: 'status',
            headerName: t('Status'),
            width: 150,
          },
        ]
      : [
          {
            field: 'conversation_language',
            headerName: t('Language'),
            width: 130,
            valueFormatter: (value?: string) => languageLabel(value, t),
          },
        ]),
  ];

  // Transform rows to include messages_count for chat conversations
  const rows = useMemo(
    () =>
      conversations.map((convo, idx) => ({
        ...convo,
        id: convo.conversation_id || `row-${idx}`,
        messages_count:
          agentType === AgentType.CHAT && Array.isArray(convo.messages)
            ? convo.messages.length
            : undefined,
      })),
    [conversations, agentType]
  );

  const data = {
    columns,
    rows,
  };

  // Export only when the page holds every row
  const canExport =
    !serverPaging || serverPaging.rowCount <= conversations.length;

  // The search box words, as the grid's quick filter holds them
  const search = serverPaging?.search ?? '';
  const filterModel = useMemo<GridFilterModel>(
    () => ({ items: [], quickFilterValues: search.split(' ').filter(Boolean) }),
    [search]
  );

  const getStatusCell = (params: GridRenderCellParams) => {
    const color =
      params.value === 'RESOLVED'
        ? 'var(--success-color)'
        : 'var(--error-text-color)';
    return (
      <span
        style={{
          color,
          textTransform: 'uppercase',
        }}
      >
        {params.value}
      </span>
    );
  };

  const getDataCollectedCell = (params: GridRenderCellParams) => {
    const sanitizedValues = Object.values(params.value)
      .filter(item => item !== null && item !== undefined)
      .map(item => String(item).trim())
      .filter(item => item !== '');
    return <span>{sanitizedValues.join(', ')}</span>;
  };

  const getMessagesCountCell = (params: GridRenderCellParams) => {
    return <span>{params.value || 0}</span>;
  };

  const getLanguageCell = (params: GridRenderCellParams) => {
    return <span>{params.formattedValue || '--'}</span>;
  };

  // NOTE: this will reformat the timestamp into a user-friendly version for
  // UI purposes-only, but timestamp value is still used by MUI Data Grid
  // for filtering.
  const getDateCell = (params: GridRenderCellParams) => {
    return formatUnixDate({ timestamp: params.value });
  };

  const gridColumns: GridColDef[] = useMemo(
    () => [
      ...data.columns
        .filter(column => visibleFields.includes(column.field))
        .map(column => ({
          ...column,
          // Only headers the backend can sort on
          sortable:
            !serverPaging || isDashboardSortable(column.field, agentType),
          renderCell:
            column.field === 'status'
              ? getStatusCell
              : column.field === 'data_collected'
                ? getDataCollectedCell
                : column.field === 'start_ts'
                  ? getDateCell
                  : column.field === 'messages_count'
                    ? getMessagesCountCell
                    : column.field === 'conversation_language'
                      ? getLanguageCell
                      : undefined,
        })),
      {
        field: 'actions',
        headerName: '', // intentionally left blank
        width: 92,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        disableExport: true,
        renderCell: params => (
          <ConversationDetailsModal item={params.row as Conversation} />
        ),
      },
    ],
    [data.columns, visibleFields, serverPaging, agentType]
  );

  return (
    <Box sx={{ width: 1 }} aria-busy={serverPaging?.isFetching || undefined}>
      <DataGrid
        // New key when the paging mode flips, or the grid keeps the old pipeline
        key={serverPaging ? 'server-paged' : 'client-paged'}
        autoHeight
        columns={gridColumns}
        disableRowSelectionOnClick
        disableColumnSelector
        disableDensitySelector
        {...(serverPaging
          ? {
              paginationMode: 'server' as const,
              rowCount: serverPaging.rowCount,
              paginationModel: serverPaging.model,
              onPaginationModelChange: serverPaging.onModelChange,
              loading: serverPaging.isFetching,
              sortingMode: 'server' as const,
              sortModel: serverPaging.sortModel,
              onSortModelChange: serverPaging.onSortModelChange,
              // Only the search box goes to the backend, the column filter is hidden
              filterMode: 'server' as const,
              filterModel,
              onFilterModelChange: (model: GridFilterModel) =>
                serverPaging.onSearchChange(
                  (model.quickFilterValues ?? []).join(' ')
                ),
              disableColumnFilter: true,
            }
          : {
              initialState: {
                sorting: { sortModel: DEFAULT_CONVERSATIONS_SORT },
                pagination: {
                  paginationModel: {
                    pageSize: DEFAULT_PAGE_SIZE,
                    page: 0,
                  },
                },
              },
            })}
        rows={data.rows}
        // Stable component, or a re-render remounts the toolbar and drops the focus
        slots={{ toolbar: ConversationsTableToolbar }}
        slotProps={{
          toolbar: {
            conversations,
            agentType,
            showExport: canExport,
            ...(serverPaging && {
              quickFilterDebounceMs: SERVER_SEARCH_DEBOUNCE_MS,
              quickFilterMaxLength: SEARCH_MAX_LENGTH,
              // say what is searched, the visible columns are not
              quickFilterPlaceholder: t('Search summary, contact or category'),
            }),
          },
        }}
        sx={{
          border: '1px solid rgba(15, 23, 42, 0.06)',
          borderRadius: 3,
          bgcolor: '#FFFFFF',
          boxShadow: 'none',
          '& .MuiDataGrid-columnHeaders': {
            borderBottom: '1px solid rgba(15, 23, 42, 0.08)',
            bgcolor: '#FFFFFF',
          },
          '& .MuiDataGrid-columnHeaderTitle': {
            color: '#5A6FAF',
            fontSize: '0.78rem',
            fontWeight: 700,
            letterSpacing: '0.01em',
          },
          '& .MuiDataGrid-cell': {
            borderBottom: '1px solid rgba(17, 24, 39, 0.06)',
          },
          '& .MuiDataGrid-row': {
            transition: 'background-color 120ms ease',
            '&:hover': {
              bgcolor: '#F9FBFF',
            },
          },
          '& .MuiDataGrid-toolbarContainer': {
            p: 1,
            borderBottom: '1px solid rgba(15, 23, 42, 0.06)',
            gap: 0.75,
          },
          '& .MuiDataGrid-footerContainer': {
            borderTop: '1px solid rgba(15, 23, 42, 0.06)',
          },
          '& .MuiDataGrid-cell:focus': {
            outline: 'none', // Removes cell focus outline
          },
          '& .MuiDataGrid-cell:focus-within': {
            outline: 'none', // Removes outline when interacting inside a cell
          },
        }}
      />
    </Box>
  );
};

export default ConversationsTable;
